import {
  connect,
  MapStateToProps,
  MapDispatchToProps,
  useSelector,
  useDispatch,
} from 'react-redux';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { VerificationRequiredActions } from '../publicSession/types';
import { getIsVerificationNeeded } from '../publicSession/selectors';
import { ComponentType, useCallback } from 'react';
import { pushVerificationPopup } from './actions';
import noop from '../utils/noop';
import { useRestrictToLoggedIn } from '../utils/hooks/useRestrictToLoggedIn';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';

export type WithVerificationProps = {
  isVerificationNeeded: boolean;
  pushVerificationPopup: (payload: VerificationRequiredActions) => void;
};

type StateProps = Pick<WithVerificationProps, 'isVerificationNeeded'>;
type DispatchProps = Pick<WithVerificationProps, 'pushVerificationPopup'>;
type OwnProps = Pick<WithVerificationProps, keyof StateProps & DispatchProps>;

const mapStateToProps: MapStateToProps<StateProps, OwnProps, {}> = state => ({
  isVerificationNeeded: getIsVerificationNeeded(state),
});
const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  pushVerificationPopup,
};

export function withVerification<TProps extends WithVerificationProps>(
  component: ComponentType<TProps>
): ComponentType<Omit<TProps, keyof WithVerificationProps>> {
  return (connect(mapStateToProps, mapDispatchToProps) as any)(component as ComponentType<any>); // redux types are not fond of generics
}

export function useVerification() {
  const isVerificationNeeded = useSelector(getIsVerificationNeeded);
  const { isLoggedIn, sendUserToJoinPage } = useRestrictToLoggedIn();
  const dispatch = useDispatch();

  const pushVerificationPopupCallback = useCallback(
    (actionType?: VerificationRequiredActions) =>
      yieldToMain(() => {
        dispatch(pushVerificationPopup(actionType));
      }),

    [dispatch]
  );

  const restrictToVerified = useCallback(
    (actionType?: VerificationRequiredActions, callback: () => void = noop) =>
      () =>
        yieldToMain(() => {
          isVerificationNeeded ? pushVerificationPopupCallback(actionType) : callback();
        }),
    [pushVerificationPopupCallback, isVerificationNeeded]
  );

  const restrictToVerifiedAndLoggedIn = useCallback(
    (
      restrictedActionName: ReferralType,
      actionType?: VerificationRequiredActions,
      callback: () => void = noop
    ) =>
      () =>
        yieldToMain(() => {
          isLoggedIn
            ? isVerificationNeeded
              ? pushVerificationPopupCallback(actionType)
              : callback()
            : sendUserToJoinPage(restrictedActionName);
        }),
    [isLoggedIn, isVerificationNeeded, pushVerificationPopupCallback, sendUserToJoinPage]
  );

  return {
    isVerificationNeeded,
    pushVerificationPopup: pushVerificationPopupCallback,
    restrictToVerified,
    restrictToVerifiedAndLoggedIn,
  };
}
